import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import CLUB from '../../config/club.js'
import { fmtVsPar } from '../../lib/scoring.js'
import { SAMPLE_TOURNAMENT } from './sampleTournament.js'
import {
  stablefordStandings, computeSkins, scrambleStandings,
  stablefordCsv, skinsCsv, scrambleCsv, STABLEFORD_MODIFIED_DEFAULT,
} from './ggFormats.js'

// G-Formats — isolated sandbox. Stableford (standard/modified) · Skins
// (carryover + validation) · Scramble (GG allowances). READ-ONLY, computed from
// the in-code SAMPLE_TOURNAMENT. Print reuses the app's window.print() pattern;
// exports are a client-side CSV Blob.
const FORMATS = [
  { key: 'stableford', label: 'Stableford' },
  { key: 'skins', label: 'Skins' },
  { key: 'scramble', label: 'Scramble' },
]

function download(name, text) {
  const blob = new Blob([text], { type: 'text/csv' })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = name
  a.click()
}

const Seg = ({ options, value, onChange }) => (
  <div className="flex items-center gap-1 rounded-lg p-1" style={{ backgroundColor: '#F3F4F6', border: '1px solid #E5E7EB' }}>
    {options.map((o) => {
      const on = value === o.value
      return (
        <button key={String(o.value)} onClick={() => onChange(o.value)}
          className="rounded-md px-3 py-1 text-xs font-bold uppercase tracking-wider transition"
          style={on ? { backgroundColor: 'var(--ge-primary)', color: '#fff' } : { backgroundColor: 'transparent', color: '#374151' }}>
          {o.label}
        </button>
      )
    })}
  </div>
)

export default function GFormats() {
  // Deep-link support: /admin/gg/formats?game=skins opens that game directly,
  // so the G-Features hub can link each game individually.
  const [params] = useSearchParams()
  const initialGame = FORMATS.some((f) => f.key === params.get('game')) ? params.get('game') : 'stableford'
  const [format, setFormat] = useState(initialGame)
  const [net, setNet] = useState(false)
  const [sfMode, setSfMode] = useState('standard')
  const [sfPoints, setSfPoints] = useState(STABLEFORD_MODIFIED_DEFAULT)
  const [carryover, setCarryover] = useState(true)
  const [validation, setValidation] = useState(false)
  const [pot, setPot] = useState(0)

  const cfg = sfMode === 'modified' ? { mode: 'modified', points: sfPoints } : { mode: 'standard' }
  const stableford = useMemo(() => stablefordStandings(SAMPLE_TOURNAMENT, { net, cfg }), [net, sfMode, sfPoints])
  const skins = useMemo(() => computeSkins(SAMPLE_TOURNAMENT, { net, carryover, validation, potPerHole: Number(pot) || 0 }), [net, carryover, validation, pot])
  const scramble = useMemo(() => scrambleStandings(SAMPLE_TOURNAMENT, { net }), [net])

  const netSeg = <Seg options={[{ value: false, label: 'Gross' }, { value: true, label: 'Net' }]} value={net} onChange={setNet} />

  return (
    <div className="card">
      <style>{PRINT_CSS}</style>
      <div className="g-screen space-y-3 mb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Seg options={FORMATS.map((f) => ({ value: f.key, label: f.label }))} value={format} onChange={setFormat} />
          <div className="flex gap-2">
            {format === 'stableford' && <button className="btn-ghost text-xs" onClick={() => download('stableford.csv', stablefordCsv(stableford))}>⬇ CSV</button>}
            {format === 'skins' && <button className="btn-ghost text-xs" onClick={() => download('skins.csv', skinsCsv(skins))}>⬇ CSV</button>}
            {format === 'scramble' && <button className="btn-ghost text-xs" onClick={() => download('scramble.csv', scrambleCsv(scramble))}>⬇ CSV</button>}
            <button className="btn-primary" onClick={() => window.print()}>🖨 Print</button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {netSeg}
          {format === 'stableford' && (
            <>
              <Seg options={[{ value: 'standard', label: 'Standard' }, { value: 'modified', label: 'Modified' }]} value={sfMode} onChange={setSfMode} />
              {sfMode === 'modified' && (
                <div className="flex flex-wrap gap-2 items-center text-xs">
                  {['eagle', 'birdie', 'par', 'bogey', 'double'].map((k) => (
                    <label key={k} className="flex items-center gap-1">
                      <span className="capitalize text-gray-500">{k}</span>
                      <input type="number" className="w-12 border rounded px-1 py-0.5" value={sfPoints[k]}
                        onChange={(e) => setSfPoints((p) => ({ ...p, [k]: Number(e.target.value) }))} />
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
          {format === 'skins' && (
            <>
              <Seg options={[{ value: true, label: 'Carryover' }, { value: false, label: 'No Carry' }]} value={carryover} onChange={setCarryover} />
              <Seg options={[{ value: false, label: 'No Validate' }, { value: true, label: 'Validate' }]} value={validation} onChange={setValidation} />
              <label className="flex items-center gap-1 text-xs"><span className="text-gray-500">Pot/hole $</span>
                <input type="number" className="w-16 border rounded px-1 py-0.5" value={pot} onChange={(e) => setPot(e.target.value)} /></label>
            </>
          )}
        </div>
      </div>

      <div className="g-print">
        <div className="g-rpt-head">
          <img src={CLUB.logo} alt={CLUB.name} className="g-rpt-logo" />
          <div>
            <div className="g-rpt-club">{CLUB.name}</div>
            <div className="g-rpt-event">{SAMPLE_TOURNAMENT.info.name} · {FORMATS.find((f) => f.key === format)?.label} · {net ? 'Net' : 'Gross'}</div>
          </div>
        </div>

        {format === 'stableford' && (
          <table className="g-tbl">
            <thead><tr><th>Pos</th><th>Team</th><th>Players</th><th>Points</th></tr></thead>
            <tbody>{stableford.map((r) => (
              <tr key={r.teamId}><td>{r.place}</td><td>{r.teamName}</td><td className="g-muted">{(r.players || []).join(' / ')}</td><td><b>{r.points}</b></td></tr>
            ))}</tbody>
          </table>
        )}

        {format === 'scramble' && (
          <table className="g-tbl">
            <thead><tr><th>Pos</th><th>Team</th><th>Players</th><th>Team Score</th><th>vs Par</th><th>Net</th></tr></thead>
            <tbody>{scramble.map((r) => (
              <tr key={r.teamId}><td>{r.place}</td><td>{r.teamName}</td><td className="g-muted">{(r.players || []).join(' / ')}</td><td>{r.teamScore}</td><td>{fmtVsPar(r.vsPar)}</td><td>{r.netScore}</td></tr>
            ))}</tbody>
          </table>
        )}

        {format === 'skins' && (
          <div className="g-two-col">
            <div>
              <h5 className="g-sub">Skins won{Number(pot) > 0 ? ' · value' : ''}</h5>
              <table className="g-tbl">
                <thead><tr><th>Team</th><th>Skins</th>{Number(pot) > 0 ? <th>Value</th> : null}</tr></thead>
                <tbody>{skins.teams.map((t) => (
                  <tr key={t.teamId}><td>{t.teamName}</td><td><b>{t.skins}</b></td>{Number(pot) > 0 ? <td>${t.value.toLocaleString()}</td> : null}</tr>
                ))}</tbody>
              </table>
              {skins.carriedAtEnd > 0 ? <p className="g-muted" style={{ marginTop: 6 }}>{skins.carriedAtEnd} skin(s) carried past hole 18 (unclaimed).</p> : null}
            </div>
            <div>
              <h5 className="g-sub">Hole-by-hole</h5>
              <table className="g-tbl">
                <thead><tr><th>Hole</th><th>Winner</th><th>Skins</th></tr></thead>
                <tbody>{skins.holes.map((h) => (
                  <tr key={h.hole}><td>{h.hole}</td><td>{h.winnerName || (h.voided ? 'void' : 'carry')}</td><td>{h.skinsAtStake}</td></tr>
                ))}</tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

const PRINT_CSS = `
  .g-rpt-head { display: flex; align-items: center; gap: 14px; padding-bottom: 10px; border-bottom: 3px solid ${CLUB.accentColor}; margin-bottom: 14px; }
  .g-rpt-logo { height: 52px; object-fit: contain; }
  .g-rpt-club { font-weight: 800; font-size: 18px; color: ${CLUB.primaryColor}; }
  .g-rpt-event { font-size: 12px; color: #555; }
  .g-two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
  .g-sub { font-size: 11px; text-transform: uppercase; letter-spacing: 0.1em; color: #888; margin: 0 0 4px; }
  .g-tbl { width: 100%; border-collapse: collapse; font-size: 12px; }
  .g-tbl th, .g-tbl td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; }
  .g-tbl th { background: #f6f6f2; color: #555; }
  .g-muted { color: #999; }
  @media print {
    body * { visibility: hidden !important; }
    .g-print, .g-print * { visibility: visible !important; }
    .g-print { position: absolute; left: 0; top: 0; width: 100%; }
    .g-screen { display: none !important; }
    @page { size: portrait; margin: 0.4in; }
  }
`
